import types from "./types";
import { firestore, MODEL_COLL } from "_firebase";
import { isEmpty, cloneDeep } from "lodash";
import * as Sentry from "@sentry/react";
//const upgrades = await firestore.doc(`${MODEL_COLL}/${modelId}`).collection('modelData').doc('upgrades').get();

import { mergeObjects, sanitizeObject } from "utils/objects";

const clearUpgradeData = () => ({
    type: types.CLEAR_UPGRADE_DATA,
});

const toggleUpgradesLoading = (isLoading) => ({
    type: types.TOGGLE_UPGRADES_LOADING,
    isLoading,
});

const setUpgradesError = (error) => ({
    type: types.SET_UPGRADES_ERROR,
    error,
});

const updateUpgradePackage = ({ packageId = "", packageUpdates = {} }) => ({
    type: types.UPDATE_UPGRADE_PACKAGE,
    packageId,
    packageUpdates,
});

const deleteUpgradePackage = ({ packageId = "" }) => ({
    type: types.DELETE_UPGRADE_PACKAGE,
    packageId,
});

const setUpgradePackages = ({ packages = {} }) => ({
    type: types.SET_UPGRADE_PACKAGES,
    packages,
});

const setInitUpgradePackages = ({ packages = {} }) => ({
    type: types.SET_INIT_UPGRADE_PACKAGES,
    packages,
});

const updatePackageUpgrades = ({ packageId, upgrades = {}, dateTime }) => ({
    type: types.UPDATE_PACKAGE_UPGRADES,
    packageId,
    upgrades,
    dateTime,
});

const updateUpgradeSelectedComponents = ({ packageId, upgradeId, newComponents = {}, rootComponentId, dateTime }) => ({
    type: types.UPDATE_UPGRADES_SELECTED_COMPONENTS,
    packageId,
    upgradeId,
    newComponents,
    rootComponentId,
    dateTime,
});

const updateUpgradeSelectedSubcomponents = ({
    packageId,
    upgradeId,
    newComponents = {},
    rootComponentId,
    parentComponentType,
    parentComponentId,
    subcomponentType,
    subcomponentId,
    dateTime,
}) => ({
    type: types.UPDATE_UPGRADES_SELECTED_SUBCOMPONENTS,
    packageId,
    upgradeId,
    newComponents,
    rootComponentId,
    parentComponentType,
    parentComponentId,
    subcomponentType,
    subcomponentId,
    dateTime,
});

const deletePackageUpgrade = ({ packageId, upgradeId }) => ({
    type: types.DELETE_PACKAGE_UPGRADE,
    packageId,
    upgradeId,
});

// !TODO PACKAGES GET BLANK WHEN SAVING AFTER DELING A PACKAGE
const saveUpgrades =
    (codes, isFromError = false) =>
    async (dispatch, getState) => {
        try {
            const {
                model: { modelId } = {},
                upgrades: { packages = {}, initPackages = {}, initPackagesSnapshot = {} } = {},
            } = getState();

            //Only don't save if both current and init are empty. Otherwise, packages may be empty if we had packages and deleted them
            if (!modelId || (isEmpty(packages) && isEmpty(initPackages))) {
                return;
            }

            const newPackages = isFromError
                ? sanitizeObject(packages, initPackages)
                : mergeObjects(packages, initPackages, initPackagesSnapshot);

            Object.keys(newPackages).forEach((packageKey) => {
                const upgradePackage = newPackages[packageKey];

                if (upgradePackage.upgrades) {
                    Object.keys(upgradePackage.upgrades).map((upgradePgk) => {
                        const fields = upgradePackage.upgrades[upgradePgk]?.fields || {};

                        if (!isEmpty(fields)) {
                            const checkIfWallAndFloor = new RegExp(Object.keys(fields).join("|")).test(/wall|floor/);

                            const codeReferences = checkIfWallAndFloor
                                ? Object.keys(fields || {})
                                      .map((key) =>
                                          Object.keys(fields?.[key] || {})
                                              .filter((k) => !!fields?.[key]?.[k]?.codeRef)
                                              .map((k) => fields[key][k].codeRef)
                                      )
                                      .flat()
                                : Object.keys(fields || {})
                                      .filter((key) => !!fields?.[key]?.codeRef)
                                      .map((key) => fields[key].codeRef);

                            const codesToAdd = Object.assign(
                                {},
                                ...codeReferences
                                    .filter((ref) => codes[ref] !== undefined)
                                    .map((ref) => ({ [ref]: codes[ref] }))
                            );

                            upgradePackage.upgrades[upgradePgk].codes = codesToAdd || {};
                        }
                    });
                }
            });

            try {
                await firestore
                    .doc(`${MODEL_COLL}/${modelId}`)
                    .collection("modelData")
                    .doc("upgrades")
                    .set(newPackages);

                if (!isFromError) {
                    dispatch(setInitUpgradePackages({ packages: newPackages }));
                    dispatch(setUpgradePackages({ packages: newPackages }));
                }
            } catch (err) {
                console.error("error saving upgrades", err);
            }
        } catch (error) {
            console.error("Error in saveUpgrades action:", error);

            Sentry.withScope((scope) => {
                const {
                    user: { email = "unknown", uid = "unknown", name = "unknown" } = {},
                    model: { modelId = "unknown", modelData = {} } = {},
                    upgrades: { packages = {} } = {},
                } = getState();

                scope.setExtras({
                    modelData: {
                        ...modelData,
                        general: {
                            ...modelData.general,
                            client: {},
                        },
                    },
                    upgrades: {
                        ...packages,
                    },
                });
                scope.setTransactionName(`Error in saveUpgrades action in model ${modelId}`);
                scope.setLevel("fatal");
                scope.setUser({
                    email,
                    uid,
                    name,
                });
                scope.captureException(error);
            });
        }
    };

const getUpgrades =
    ({ modelId }) =>
    async (dispatch) => {
        dispatch(toggleUpgradesLoading(true));
        const upgrades = await firestore.doc(`${MODEL_COLL}/${modelId}`).collection("modelData").doc("upgrades").get();
        const packages = upgrades.data();

        dispatch(setInitUpgradePackages({ packages }));
        dispatch(setUpgradePackages({ packages }));
        dispatch(toggleUpgradesLoading(false));
    };

const updateUpgradeCodes =
    ({ newCodeRef, oldCodeRef, values = {}, fieldName }) =>
    (dispatch, getState) => {
        const { upgrades: { packages = {} } = {} } = getState();

        if (isEmpty(packages)) {
            //TODO: LAUREN - In this case there are no upgrades. Can I just put a return statement like this?
            return;
        }

        //If we get to this point, we assume that there are upgrades present

        // - Go into each upgrade in each package
        const updatedPackages = cloneDeep(packages);
        Object.keys(packages).forEach((packageKey) => {
            const { upgrades = {} } = packages[packageKey];
            // - In each upgrade, search fields for the code type with the oldCodeRef value
            Object.keys(upgrades).forEach((upgradeKey) => {
                const {
                    fields: {
                        [fieldName]: { codeRef = "" } = {},
                        wall: {
                            [fieldName]: { codeRef: foundationWallCodeRef = "" } = {},
                            ponyWall: { [fieldName]: { codeRef: ponyWallCodeRef = "" } = {} } = {},
                            ponyWall = {},
                        } = {},
                        wall = {},
                        floor: { [fieldName]: { codeRef: foundationFloorCodeRef = "" } = {} } = {},
                        floor = {},
                    } = {},
                    fields = {},
                } = upgrades[upgradeKey];

                // - If found, update this field and its RValues to be the new ones coming in
                // - Values will be an obj with the fields that need changed - can spread them into the root of the fields obj
                if (codeRef === oldCodeRef) {
                    //Typical flat component
                    updatedPackages[packageKey].upgrades[upgradeKey].fields = {
                        ...fields,
                        ...values,
                    };
                } else if (foundationWallCodeRef === oldCodeRef) {
                    //Spread changes into foundation wall
                    updatedPackages[packageKey].upgrades[upgradeKey].fields.wall = { ...wall, ...values };
                } else if (foundationFloorCodeRef === oldCodeRef) {
                    //Spread changes into foundation floor
                    updatedPackages[packageKey].upgrades[upgradeKey].fields.floor = { ...floor, ...values };
                } else if (ponyWallCodeRef === oldCodeRef) {
                    //Spread changes into foundation ponywall
                    updatedPackages[packageKey].upgrades[upgradeKey].fields.wall.ponyWall = { ...ponyWall, ...values };
                }
            });
        });

        dispatch(setUpgradePackages({ packages: updatedPackages }));
    };

const reorderUpgradePackages = (packages = []) => ({
    type: types.UPDATE_UPGRADE_PACKAGE_ORDER,
    packages,
});

const updateUpgradeCost = ({ packageId, upgradeId, upgradeType, cost, items }) => ({
    type: types.UPDATE_UPGRADE_COST,
    packageId,
    upgradeId,
    upgradeType,
    cost,
    items,
});

const updatePackageOperatingConditions = ({ packageId, operatingConditions }) => ({
    type: types.UPDATE_PACKAGE_OPERATING_CONDITIONS,
    packageId,
    operatingConditions,
});

const updatePackageBaseCase = ({ packageId, relativeBaseCase }) => ({
    type: types.UPDATE_PACKAGE_BASE_CASE,
    packageId,
    relativeBaseCase,
});

const updateUpgradeSnapshot = (upgrades) => ({
    type: types.UPDATE_UPGRADE_SNAPSHOT_SUCCESS,
    upgrades,
});

export default {
    clearUpgradeData,
    toggleUpgradesLoading,
    setUpgradesError,
    updateUpgradePackage,
    setInitUpgradePackages,
    saveUpgrades,
    getUpgrades,
    setUpgradePackages,
    deleteUpgradePackage,
    updatePackageUpgrades,
    updateUpgradeSelectedComponents,
    updateUpgradeSelectedSubcomponents,
    deletePackageUpgrade,
    updateUpgradeCodes,
    reorderUpgradePackages,
    updateUpgradeCost,
    updatePackageOperatingConditions,
    updatePackageBaseCase,
    updateUpgradeSnapshot,
};
